// src/components/wall/timerComponent.jsx
import React, { useEffect, useRef, useState } from "react";

const LS_KEY_TIMER_STATE = "wall:timer_state";

export function TimerComponent() {
  const [title, setTitle] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);
  const rafRef = useRef(null);
  const deadlineRef = useRef(null);
  const durationRef = useRef(null);
  const reverseRef = useRef(false);
  const pausedRef = useRef(false);

  const stopAll = () => {
    clearInterval(intervalRef.current);
    cancelAnimationFrame(rafRef.current);
    intervalRef.current = null;
    rafRef.current = null;
  };

  const formatTime = (seconds) => {
    if (seconds == null) return "--:--";
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.max(0, seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  const persist = (obj) => {
    try {
      localStorage.setItem(LS_KEY_TIMER_STATE, JSON.stringify(obj));
    } catch {}
  };
  const clearPersist = () => {
    try {
      localStorage.removeItem(LS_KEY_TIMER_STATE);
    } catch {}
  };

  const renderPrepared = ({ title, duration, reverse }) => {
    stopAll();
    setTitle(title || null);
    durationRef.current = duration;
    reverseRef.current = !!reverse;
    pausedRef.current = true;
    setTimeLeft(duration);
    setProgress(reverse ? 1 : 0);
    persist({
      title: title || null,
      duration,
      reverse: !!reverse,
      paused: true,
      startedAt: null,
    });
  };

  const startTimer = ({ title, duration, reverse = false, startedAt }) => {
    if (!duration || duration <= 0) return;

    stopAll();
    setTitle(title || null);
    durationRef.current = duration;
    reverseRef.current = !!reverse;
    pausedRef.current = false;

    const startMs =
      startedAt && Number.isFinite(startedAt) ? startedAt : Date.now();
    deadlineRef.current = startMs + duration * 1000;

    persist({
      title: title || null,
      duration,
      reverse: !!reverse,
      paused: false,
      startedAt: startMs,
    });

    const tickSeconds = () => {
      const now = Date.now();
      const remainingMs = Math.max(0, deadlineRef.current - now);
      const remainingSec = Math.round(remainingMs / 1000);
      setTimeLeft(remainingSec);
      if (remainingSec === 0) {
        stopAll();
        clearPersist();
      }
    };

    const tickProgress = () => {
      const now = Date.now();
      const total = durationRef.current * 1000;
      const elapsed = Math.min(
        total,
        Math.max(0, now - (deadlineRef.current - total))
      );
      const pFill = total === 0 ? 1 : elapsed / total;
      const p = reverseRef.current ? 1 - pFill : pFill;
      setProgress(p);
      if (pFill < 1) rafRef.current = requestAnimationFrame(tickProgress);
    };

    tickSeconds();
    tickProgress();
    intervalRef.current = setInterval(tickSeconds, 200);
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY_TIMER_STATE);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.paused) {
          renderPrepared({
            title: saved.title,
            duration: saved.duration,
            reverse: !!saved.reverse,
          });
        } else if (saved?.startedAt && saved?.duration) {
          const endAt = saved.startedAt + saved.duration * 1000;
          const now = Date.now();
          if (endAt > now) {
            startTimer(saved);
          } else {
            clearPersist();
          }
        }
      }
    } catch {}

    const handle = (payload = {}) => {
      const { mode, title, duration, reverse = false, startedAt } = payload;

      if (mode === "reset") {
        stopAll();
        clearPersist();
        setTitle(title || null);
        setTimeLeft(0);
        setProgress(0);
        return;
      }

      if (mode === "prepare") {
        if (!duration || duration <= 0) return;
        renderPrepared({ title, duration, reverse });
        return;
      }

      if (duration && duration > 0) {
        startTimer({ title, duration, reverse, startedAt });
        return;
      }

      try {
        const raw = localStorage.getItem(LS_KEY_TIMER_STATE);
        if (raw) {
          const saved = JSON.parse(raw);
          if (saved?.paused && saved?.duration) {
            startTimer({
              title: saved.title,
              duration: saved.duration,
              reverse: !!saved.reverse,
              startedAt: Date.now(),
            });
          }
        }
      } catch {}
    };

    const handleShowTimer = (e) => handle(e.detail || {});
    window.addEventListener("SHOW_TIMER", handleShowTimer);

    let bc;
    if (typeof window !== "undefined") {
      bc = new BroadcastChannel("operator-events");
      bc.onmessage = (e) => {
        const { actionId, payload } = e.data || {};
        if (actionId === "SHOW_TIMER") handle(payload || {});
      };
    }

    return () => {
      stopAll();
      window.removeEventListener("SHOW_TIMER", handleShowTimer);
      if (bc) bc.close();
    };
  }, []);

  const ringSize = 540;
  const ringThickness = 40;
  const angle = Math.round(progress * 360);
  const gradient =
    " linear-gradient(245.16deg, rgba(151, 0, 0, 0.5548) 19.95%, rgba(255, 53, 0, 0.76) 49.36%)";
  const maskRing = `radial-gradient(circle at center, transparent ${
    ringSize / 2 - ringThickness
  }px, #000 ${ringSize / 2 - ringThickness + 1}px)`;
  const maskProgress = `conic-gradient(#000 0deg ${angle}deg, transparent ${angle}deg 360deg)`;

  return (
    <div
      className="relative flex flex-col items-center justify-center w-screen h-full"
      style={{
        animation: "timerEnter 560ms cubic-bezier(0.25,0.8,0.25,1) both",
      }}
    >
      <img
        src="/timer/background.svg"
        className="absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] w-screen h-screen object-cover"
        alt=""
      />
      <img
        src="/timer/inner-circle.svg"
        className="absolute z-[11] left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2"
        style={{
          animation: "rotateLoop 12s linear infinite",
          transformOrigin: "50% 50%",
        }}
        alt=""
      />
      <img
        src="/timer/inner-circle2.svg"
        className="absolute z-[10] left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2"
        style={{
          animation: "rotateLoop2 12s linear infinite",
          transformOrigin: "50% 50%",
        }}
        alt=""
      />
      <img
        src="/timer/outer-circle.svg"
        className="absolute z-[12] left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2"
        alt=""
      />
      <div
        className="absolute left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2 text-white"
        style={{ fontFamily: "Etemad" }}
      >
        {title && (
          <div className="text-center text-[24px] mb-2 opacity-90">{title}</div>
        )}
        <div className="text-center font-normal text-[104px] leading-none">
          {formatTime(timeLeft)}
        </div>
      </div>
      <div
        className="absolute left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: `${ringSize}px`,
          height: `${ringSize}px`,
          backgroundImage: gradient,
          WebkitMaskImage: `${maskProgress}, ${maskRing}`,
          maskImage: `${maskProgress}, ${maskRing}`,
          WebkitMaskComposite: "source-in",
          maskComposite: "intersect",
          transition: "all 120ms linear",
        }}
      />
      <style jsx global>{`
        @keyframes rotateLoop {
          from {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }
        @keyframes rotateLoop2 {
          from {
            transform: translate(-50%, -50%) rotate(360deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(0deg);
          }
        }
        @keyframes timerEnter {
          0% {
            opacity: 0;
            transform: translateY(24px) scale(0.995);
            filter: blur(6px);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0px);
          }
        }
      `}</style>
    </div>
  );
}

export default TimerComponent;
